import { watch } from 'vue'

import { useCurrentUserStore } from '@common/current-user-layer/stores/currentUser.store'
import { useLocationStore } from '@common/location-layer/store/location.store'
import { setLocale, isLocaleSupported } from '@ghl-revex/assets-drm-client/setup'

import type { NuxtApp } from 'nuxt/app'

const DEFAULT_LOCALE = 'en'

/**
 * The language the embedded SDKs (assets-drm, meeting-client) render in: the user's profile
 * choice, then the portal builder's, then English. Not narrowed to the host i18n's `localeCodes`
 * — each SDK ships its own catalog and skips a code it does not support.
 */
export const resolveSdkLanguage = (): string =>
  useCurrentUserStore().profile?.languagePreference || useLocationStore().languagePreferenceBuilder || DEFAULT_LOCALE

/**
 * The slice of Nuxt i18n's injected composer this plugin actually uses.
 *
 * `nuxtApp.$i18n` is `@nuxtjs/i18n`'s augmented composer, and it carries both `localeCodes` and
 * `setLocale` at runtime — the module's own `route-locale-detect` plugin calls
 * `$i18n.setLocale(...)`. vue-i18n 11 does not declare either member on its exported `Composer`
 * type, so typing this as `ReturnType<typeof useI18n>` reports TS2339 for `localeCodes` and TS2551
 * for `setLocale` even though both exist. Declaring the shape structurally keeps the plugin honest
 * about exactly what it depends on instead of widening to `any`.
 *
 * `localeCodes` and `setLocale` stay optional because the runtime access below is already
 * defensive, and that must not change: if a future major does drop either, the plugin degrades to
 * leaving detection in charge rather than throwing during boot.
 */
interface LocalePreferenceI18n {
  locale: string | { value: string }
  localeCodes?: string[] | { value: string[] }
  setLocale?: (locale: string) => Promise<void> | void
}

export const createLocalePreferencePlugin = () => {
  return (nuxtApp: NuxtApp) => {
    const currentUserStore = useCurrentUserStore()
    const locationStore = useLocationStore()
    const i18n = nuxtApp.$i18n as LocalePreferenceI18n | undefined
    if (!i18n) return

    // `localeCodes` is a plain array on some paths and a ref on others, so narrow rather than
    // reaching for `.value` — the previous `?.value ?? codes` form was unsound on the array branch.
    const rawLocaleCodes = i18n.localeCodes
    const supportedLocales = new Set(Array.isArray(rawLocaleCodes) ? rawLocaleCodes : (rawLocaleCodes?.value ?? []))

    /**
     * Locale explicitly stored for this user/portal, or `null` when none exists.
     *
     * `null` deliberately does NOT mean English — it means "nobody chose a language", so
     * @nuxtjs/i18n `detectBrowserLanguage` (cookie, then `navigator.languages`) stays in charge.
     */
    const resolveStoredLocale = (): string | null => {
      const userPref = currentUserStore.profile?.languagePreference
      if (userPref && supportedLocales.has(userPref)) return userPref

      const builderPref = locationStore.languagePreferenceBuilder
      if (builderPref && supportedLocales.has(builderPref)) return builderPref

      return null
    }

    const applyDrmLocale = async () => {
      try {
        const lang = resolveSdkLanguage()
        if (isLocaleSupported(lang)) {
          setLocale(lang)
        }
      } catch (error) {
        console.error('Failed to set assets drm client locale:', error)
      }
    }

    let isApplying = false
    /**
     * Set when a re-apply is requested while one is already in flight. Without it the
     * request is dropped: `i18n.setLocale` loads locale files asynchronously, so a
     * higher-priority preference arriving mid-switch (profile loading after the portal
     * builder's) — or a detection event firing mid-switch — would be discarded and
     * nothing would retry, stranding the user on the earlier locale.
     */
    let isApplyQueued = false

    const applyLocale = async () => {
      const target = resolveStoredLocale()
      void applyDrmLocale()
      if (!target) return

      if (isApplying) {
        isApplyQueued = true
        return
      }

      const current = typeof i18n.locale === 'string' ? i18n.locale : i18n.locale?.value
      if (target === current) return

      isApplying = true
      try {
        await i18n.setLocale?.(target)
      } finally {
        isApplying = false

        if (isApplyQueued) {
          isApplyQueued = false
          // Re-resolve rather than reusing `target`: the queued request exists precisely
          // because the answer may have changed while the switch was in flight.
          await applyLocale()
        }
      }
    }

    void applyLocale()

    watch(
      () => currentUserStore.profile?.languagePreference,
      () => void applyLocale()
    )

    watch(
      () => locationStore.languagePreferenceBuilder,
      () => void applyLocale()
    )

    /**
     * `@nuxtjs/i18n` registers a global `locale-changing` route middleware
     * (runtime/plugins/route-locale-detect.js) that re-runs browser/cookie detection on the
     * first navigation — i.e. AFTER this plugin has run. Without this hook that detection
     * overwrites a stored preference, so a user whose profile says English lands on the
     * browser's language instead. Re-assert only when a stored preference exists; when there
     * is none, whatever detection resolved is kept.
     */
    nuxtApp.hook('i18n:localeSwitched', ({ newLocale }: { newLocale: string }) => {
      const target = resolveStoredLocale()
      if (!target || target === newLocale) return
      void applyLocale()
    })
  }
}
